import { api } from '@/lib/api'

export const CMS_FIELD_TYPES = ['text', 'textarea', 'richtext', 'number', 'boolean', 'date', 'image', 'link', 'select'] as const
export type CmsFieldType = (typeof CMS_FIELD_TYPES)[number]

export const CMS_FIELD_TYPE_LABELS: Record<CmsFieldType, string> = {
  text: 'Plain Text',
  textarea: 'Multi-line Text',
  richtext: 'Rich Text',
  number: 'Number',
  boolean: 'Boolean (Yes/No)',
  date: 'Date',
  image: 'Image URL',
  link: 'Link',
  select: 'Option (Select)',
}

export interface CmsFieldDefinition {
  key: string
  label: string
  type: CmsFieldType
  required?: boolean
  options?: string[]
  helpText?: string
}

export interface CmsCollection {
  id: string
  key: string
  name: string
  description?: string
  fields: CmsFieldDefinition[]
  hasSlug: boolean
  createdAt: string
  updatedAt: string
}

export type CmsItemStatus = 'draft' | 'published'

export interface CmsItem {
  id: string
  collectionId: string
  slug?: string
  fieldValues: Record<string, unknown>
  status: CmsItemStatus
  isFeatured: boolean
  order: number
  publishedAt?: string
  lastModifiedBy?: string
  createdAt: string
  updatedAt: string
}

export interface CmsItemListResponse {
  items: CmsItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CmsItemQueryParams {
  page?: number
  limit?: number
  search?: string
  status?: CmsItemStatus
  filter?: Record<string, unknown>
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

export interface CreateCollectionData {
  key: string
  name: string
  description?: string
  fields: CmsFieldDefinition[]
  hasSlug?: boolean
}

export type UpdateCollectionData = Partial<Omit<CreateCollectionData, 'key'>>

export interface CreateItemData {
  slug?: string
  fieldValues: Record<string, unknown>
  status?: CmsItemStatus
  isFeatured?: boolean
  order?: number
  publishedAt?: string
}

export type UpdateItemData = Partial<CreateItemData>

function buildItemQuery(params: CmsItemQueryParams): string {
  const qp = new URLSearchParams()
  if (params.page) qp.append('page', String(params.page))
  if (params.limit) qp.append('limit', String(params.limit))
  if (params.search) qp.append('search', params.search)
  if (params.status) qp.append('status', params.status)
  if (params.filter) qp.append('filter', JSON.stringify(params.filter))
  if (params.sortBy) qp.append('sortBy', params.sortBy)
  if (params.sortOrder) qp.append('sortOrder', params.sortOrder)
  return qp.toString()
}

export class CmsCollectionService {
  static async list(): Promise<CmsCollection[]> {
    return api.get<CmsCollection[]>('/admin/cms/collections')
  }

  static async get(id: string): Promise<CmsCollection> {
    return api.get<CmsCollection>(`/admin/cms/collections/${id}`)
  }

  static async getByKey(key: string): Promise<CmsCollection> {
    const all = await this.list()
    const found = all.find((c) => c.key === key)
    if (!found) throw new Error(`Collection "${key}" not found`)
    return found
  }

  static async create(data: CreateCollectionData): Promise<CmsCollection> {
    return api.post<CmsCollection>('/admin/cms/collections', data)
  }

  static async update(id: string, data: UpdateCollectionData): Promise<CmsCollection> {
    return api.put<CmsCollection>(`/admin/cms/collections/${id}`, data)
  }

  static async remove(id: string): Promise<void> {
    return api.delete(`/admin/cms/collections/${id}`)
  }
}

export class CmsItemService {
  static async list(collectionId: string, params: CmsItemQueryParams = {}): Promise<CmsItemListResponse> {
    const query = buildItemQuery(params)
    const endpoint = query
      ? `/admin/cms/collections/${collectionId}/items?${query}`
      : `/admin/cms/collections/${collectionId}/items`
    return api.get<CmsItemListResponse>(endpoint)
  }

  static async get(id: string): Promise<CmsItem> {
    return api.get<CmsItem>(`/admin/cms/items/${id}`)
  }

  static async create(collectionId: string, data: CreateItemData): Promise<CmsItem> {
    return api.post<CmsItem>(`/admin/cms/collections/${collectionId}/items`, data)
  }

  static async update(id: string, data: UpdateItemData): Promise<CmsItem> {
    return api.put<CmsItem>(`/admin/cms/items/${id}`, data)
  }

  static async remove(id: string): Promise<void> {
    return api.delete(`/admin/cms/items/${id}`)
  }

  static async setStatus(id: string, status: CmsItemStatus): Promise<CmsItem> {
    return api.patch<CmsItem>(`/admin/cms/items/${id}/status`, { status })
  }

  static async bulkAction(
    collectionId: string,
    ids: string[],
    action: 'delete' | 'publish' | 'unpublish',
  ): Promise<{ affected: number }> {
    return api.post(`/admin/cms/collections/${collectionId}/items/bulk`, { ids, action })
  }

  static async reorder(collectionId: string, items: { id: string; order: number }[]): Promise<void> {
    return api.post(`/admin/cms/collections/${collectionId}/items/reorder`, { items })
  }
}
